// @ts-check
/* eslint-env browser */

/**
 * Web component for displaying a modal dialog.
 * Callers are responsible for providing the full dialog content as inner HTML,
 * including a <dialog> element with [data-action="cancel"] and optionally
 * [data-action="confirm"] buttons.
 *
 * Usage:
 *   <modal-dialog>
 *     <dialog class="modal-dialog">
 *       <h2 class="modal-dialog__title govuk-heading-m">My title</h2>
 *       <p class="govuk-body">My message</p>
 *       <div class="govuk-button-group">
 *         <button class="govuk-button govuk-button--warning" data-action="confirm">Confirm</button>
 *         <button class="govuk-button govuk-button--secondary" data-action="cancel">Cancel</button>
 *       </div>
 *     </dialog>
 *   </modal-dialog>
 */
export class ModalDialog extends HTMLElement {
  /** @type {HTMLElement | null} */
  #returnFocusTo = null
  /** @type {{ element: HTMLElement, wasInert: boolean }[]} */
  #inertedElements = []

  connectedCallback () {
    this.#returnFocusTo = /** @type HTMLElement */(document.activeElement)
    this.#setupEventListeners()
    this.#show()
  }

  disconnectedCallback () {
    this.#removeBackgroundInert()
  }

  /**
   * Sets up event listeners for the dialog
   */
  #setupEventListeners () {
    const dialog = this.querySelector('dialog')
    if (!dialog) return

    // Handle ESC key to close dialog
    dialog.addEventListener('cancel', this.#handleCancel)

    // Handle backdrop clicks (clicking outside the dialog)
    dialog.addEventListener('click', this.#handleBackdropClick)

    this.querySelectorAll('[data-action="confirm"]').forEach(el => el.addEventListener('click', this.#handleConfirm))
    this.querySelectorAll('[data-action="cancel"]').forEach(el => el.addEventListener('click', this.#handleCancel))
  }

  /**
   * Shows the dialog
   */
  #show () {
    const dialog = this.querySelector('dialog')
    if (dialog instanceof HTMLDialogElement) {
      // set the dialog non modally during an onboarding tour so that intro.js can highlight it and render its tooltip on top.
      const tourActive = Boolean(document.querySelector('.introjs-tour'))
      if (tourActive) {
        dialog.classList.add('modal-dialog--tour')
        dialog.show()
      } else {
        dialog.showModal()
      }
      this.setAttribute('open', '')
      if (!tourActive) {
        this.#inertBackground()
      }

      // During a tour, let intro.js manage focus so it isn't pulled to a dialog
      // button before the tooltip renders.
      if (!tourActive) {
        const target = /** @type {HTMLElement | null} */ (dialog.querySelector('h1, h2, h3, h4, h5, h6, p, a, button'))
        if (target?.matches('h1, h2, h3, h4, h5, h6, p')) target.tabIndex = -1
        target?.focus()
      }
    }
  }

  /**
   * Closes and removes the dialog, returning focus to the element that opened it.
   */
  close () {
    this.#removeBackgroundInert()
    this.remove()
    this.#returnFocusTo?.focus()
  }

  /**
   * Makes all top-level page content inert except this modal host.
   */
  #inertBackground () {
    const bodyChildren = Array.from(document.body.children)
    bodyChildren.forEach((el) => {
      if (!(el instanceof HTMLElement)) return
      if (el === this) return
      this.#inertedElements.push({ element: el, wasInert: el.inert })
      el.inert = true
    })
  }

  /**
   * Restores prior inert state for page content.
   */
  #removeBackgroundInert () {
    this.#inertedElements.forEach(({ element, wasInert }) => {
      element.inert = wasInert
    })
    this.#inertedElements = []
  }

  /**
   * Handles confirm action
   * @param {Event} event
   */
  #handleConfirm = (event) => {
    const confirmButton = event.currentTarget
    const isAsync = confirmButton instanceof HTMLElement && confirmButton.hasAttribute('data-async-confirm')

    this.dispatchEvent(new CustomEvent('confirm', { bubbles: true }))

    if (!isAsync) {
      this.close()
    }
  }

  /**
   * Handles cancel action
   */
  #handleCancel = () => {
    this.dispatchEvent(new CustomEvent('cancel', { bubbles: true }))
    this.close()
  }

  /**
   * Handles backdrop clicks by checking if the dialog element itself was clicked
   * @param {MouseEvent} evt
   */
  #handleBackdropClick = (evt) => {
    const dialog = this.querySelector('dialog')
    if (evt.target === dialog) {
      this.#handleCancel()
    }
  }
}

customElements.define('modal-dialog', ModalDialog)
